import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  Button,
  Link,
  Tabs,
  Tab,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  IconButton,
  Pagination,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerProfiles, selectCustomerProfiles } from '../../redux/features/customerProfileSlice';
import useMercari from './useMercari';
import MercariService from './mercariService';
import { format } from 'date-fns';
import io from 'socket.io-client';
import { useAuth } from '../../context/AuthContext';
import ProfileCombo from '../../sharedComponents/ProfileCombo';
import OrderByOptionService from '../setting/orderByOption/OrderByOptionService';
import { toast } from 'react-toastify';
import useCustomerProfile from '../customerProfile/useCustomerProfile';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Translate from '../keyData/modalMenu/translate/Translate';

const MercariChange = () => {
  // Redux integration for customer profiles
  const dispatch = useDispatch();
  const profiles = useSelector(selectCustomerProfiles);
  const shopName = useSelector((state) => state.shop.name);

  // State for tabs
  const [tabValue, setTabValue] = useState(0);
  
  // Existing states
  const { 
    pendingChanges, 
    error, 
    getPendingChanges, 
    takeCase, 
    completeCase,
    negotiationLists,
    isLoadingNegotiations,
    negotiationError,
    negotiationPagination,
    getNegotiationLists,
    addNegotiation,
    untrackNegotiation,
    updateNegotiationCustomer
  } = useMercari();
  const { authState } = useAuth();
  const currentUsername = authState.user?.nickname;
  const [takingCaseId, setTakingCaseId] = useState(null);
  const [completingCaseId, setCompletingCaseId] = useState(null);
  // The "ID ที่ต่อราคา" picker lists every active order-by option, not only Mercari
  // accounts (owner 2026-10-30): any active clicker/account can be the one negotiating.
  const [orderByOptions, setOrderByOptions] = useState([]);
  // The hook's `isLoading` is shared by take, complete AND every list refresh
  // (including the one another admin's socket event triggers), and the page
  // early-returned a spinner on it — wiping a half-typed negotiation form
  // whenever anyone touched a case. The spinner is for the first load only (E2).
  const [initialLoading, setInitialLoading] = useState(true);
  // The hook's `error` is never cleared; it used to replace the page (E3).
  const [dismissedError, setDismissedError] = useState(null);

  // Simplified negotiation state: each item has url + target price; urgentPurchase once per form
  const [newNegotiation, setNewNegotiation] = useState({
    items: [{ url: '', price: '' }],
    smileName: '',
    urgentPurchase: false,
    orderByOptionId: ''
  });

  // Negotiation list filters and pagination
  const [showHiddenItems, setShowHiddenItems] = useState(false); // Controls both sold out and untracked
  const [currentPage, setCurrentPage] = useState(1);
  const [untrackSearchUrl, setUntrackSearchUrl] = useState('');
  // "Who asked for a discount on this link?" — admin holds only the Mercari link
  // (seller replied inside Mercari) and needs the customer behind it.
  const [lookupUrl, setLookupUrl] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [untrackingId, setUntrackingId] = useState(null);
  const { updateSocialId } = useCustomerProfile();
  const [socialIdDialogOpen, setSocialIdDialogOpen] = useState(false);
  const [currentSocialId, setCurrentSocialId] = useState('');
  const [currentCustomerId, setCurrentCustomerId] = useState(null);
  const [translateIsOpen, setTranslateIsOpen] = useState(false);

  // Existing socket connection
  useEffect(() => {
    getPendingChanges().finally(() => setInitialLoading(false));

    // withCredentials: the namespace admits admins only, proven by the
    // admin_token cookie on the handshake (B5).
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}/mercari-negotiation`, { withCredentials: true });
  
    socket.on('connect', () => {
      console.log('Connected to Mercari Admin Socket.IO');
      console.log('Socket ID:', socket.id);
    });
  
    socket.on('disconnect', () => {
      console.log('Disconnected from Mercari Admin Socket.IO');
    });
  
    socket.on('negotiation_case_taken', ({ changeId, adminId, adminName }) => {
      console.log('Case taken event received:', { changeId, adminId, adminName });
      getPendingChanges();
    });
  
    socket.on('negotiation_case_completed', (data) => {
      console.log('Case completed event received:', data);
      getPendingChanges();
    });
  
    return () => {
      socket.disconnect();
    };
  }, []);

  // Existing handlers
  const handleUrlClick = async (changeId, url) => {
    if (!changeId) return;
    
    setTakingCaseId(changeId);
    try {
      const success = await takeCase(changeId);
      if (success) {
        getPendingChanges();
        window.open(url, '_blank');
      }
    } finally {
      setTakingCaseId(null);
    }
  };

  const handleCompleteCase = async (changeId) => {
    setCompletingCaseId(changeId);
    try {
      const success = await completeCase({changeId, status: 'informed'});
      if (success) {
        await getPendingChanges();
      }
    } finally {
      setCompletingCaseId(null);
    }
  };

  // New handlers for price negotiation
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const renderChangeDetails = (change) => {
    if (change.changeType === 'price_change') {
      const isIncrease = change.newValue > change.oldValue;
      return (
        <Typography
          variant="body2"
          color={isIncrease ? 'error.main' : 'success.main'}
        >
          ¥{change.oldValue} → ¥{change.newValue}
        </Typography>
      );
    }

    if (change.changeType === 'new_comment') {
      return (
        <Typography variant="body2">
          New Comment
        </Typography>
      );
    }    return null;
  }

  // Add useEffect for negotiation lists - fixed to prevent double refresh
  useEffect(() => {
    if (tabValue === 1) {
      getNegotiationLists(currentPage, showHiddenItems, showHiddenItems);
      if (orderByOptions.length === 0) {
        OrderByOptionService.getActiveOrderByOptions()
          .then((res) => setOrderByOptions(res.data || []))
          .catch(() => setOrderByOptions([]));
      }
    }
  }, [tabValue, currentPage, showHiddenItems]);

  // Add useEffect to fetch customer profiles
  useEffect(() => {
    dispatch(getCustomerProfiles(shopName));
  }, [dispatch, shopName]);

  // Update handleNegotiationSubmit
  const [resetKey, setResetKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNegotiationSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting) return;
    
    // Filter to items with non-empty URL
    const validItems = newNegotiation.items
      .filter(item => item.url.trim() !== '')
      .map(item => ({
        url: item.url.trim(),
        price: item.price != null && String(item.price).trim() !== '' ? String(item.price).trim() : ''
      }));
    if (validItems.length === 0) return;

    setIsSubmitting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const result = await addNegotiation({
        items: validItems,
        smileName: newNegotiation.smileName || '',
        urgentPurchase: newNegotiation.urgentPurchase,
        orderByOptionId: newNegotiation.orderByOptionId || null
      });
      
      if (result) {
        // Show toast messages for results
        if (result.results && result.results.length > 0) {
          const retrackedCount = result.results.filter(r => r.message === 'เริ่มติดตามรายการอีกครั้ง').length;
          const newCount = result.results.length - retrackedCount;
          
          if (retrackedCount > 0 && newCount > 0) {
            toast.success(`เพิ่มรายการใหม่ ${newCount} รายการ และเริ่มติดตามอีกครั้ง ${retrackedCount} รายการ`);
          } else if (retrackedCount > 0) {
            toast.success(`เริ่มติดตามอีกครั้ง ${retrackedCount} รายการ`);
          } else {
            toast.success(`เพิ่มรายการสำเร็จ ${newCount} รายการ`);
          }
        }

        // Warn if customer is Line but has no socialId (so they can add it for Chatcone)
        if (result.lineNoSocialIdWarning && result.lineNoSocialIdMessage) {
          toast.warning(result.lineNoSocialIdMessage);
        }

        // Show errors (e.g. "รายการนี้โดนเพิ่มไปแล้ว", "ไม่พบรายการสินค้า") so user sees why it failed
        if (result.errors && result.errors.length > 0) {
          result.errors.forEach(err => {
            toast.error(`${err.url}: ${err.error}`);
          });
        }

        // Only reset form and refresh list when at least one item succeeded
        if (result.results && result.results.length > 0) {
          setNewNegotiation({
            items: [{ url: '', price: '' }],
            smileName: '',
            urgentPurchase: false,
            orderByOptionId: ''
          });
          setResetKey(prev => prev + 1);
          await getNegotiationLists(currentPage, showHiddenItems, showHiddenItems);
        }
      } 
    } catch (error) {
      console.error('Error submitting negotiation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLookupByUrl = async () => {
    if (!lookupUrl.trim()) return;
    setIsLookingUp(true);
    try {
      const response = await MercariService.lookupNegotiationsByItem(lookupUrl.trim());
      setLookupResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'ค้นหาไม่สำเร็จ');
    } finally {
      setIsLookingUp(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('คัดลอกแล้ว');
    } catch {
      toast.error('คัดลอกไม่สำเร็จ');
    }
  };

  const handleUntrackByUrl = async () => {
    if (!untrackSearchUrl.trim()) return;
    
    // Use a temporary ID for loading state
    const tempId = 'searching';
    setUntrackingId(tempId);
    try {
      // Pass null for favoriteId and the URL - backend will find by URL
      const result = await untrackNegotiation(null, untrackSearchUrl.trim());
      if (result?.success) {
        setUntrackSearchUrl('');
        toast.success('เลิกติดตามรายการสำเร็จ');
        // Refresh the list
        await getNegotiationLists(currentPage, showHiddenItems, showHiddenItems);
      } else {
        // Show appropriate message
        if (result?.action === 'already_untracked') {
          toast.info('รายการนี้ถูกเลิกติดตามแล้ว');
        } else {
          toast.error(result?.message || 'ไม่พบรายการที่ต้องการ');
        }
      }
    } catch (error) {
      toast.error('เกิดข้อผิดพลาดในการดำเนินการ');
    } finally {
      setUntrackingId(null);
    }
  };

  const handleUntrackById = async (favoriteId, isAlreadyUntracked = false) => {
    // If already untracked, inform user
    if (isAlreadyUntracked) {
      toast.info('รายการนี้ถูกเลิกติดตามแล้ว');
      return;
    }
    // One tap used to untrack with no confirmation (E8).
    if (!window.confirm('ยืนยันเลิกติดตามรายการนี้?')) return;

    setUntrackingId(favoriteId);
    try {
      const result = await untrackNegotiation(favoriteId);
      if (result?.success) {
        toast.success('เลิกติดตามรายการสำเร็จ');
        // Refresh the list
        await getNegotiationLists(currentPage, showHiddenItems, showHiddenItems);
      } else {
        // Check if already untracked
        if (result?.action === 'already_untracked') {
          toast.info('รายการนี้ถูกเลิกติดตามแล้ว');
        } else {
          toast.error(result?.message || 'ไม่สามารถดำเนินการได้');
        }
      }
    } catch (error) {
      toast.error('เกิดข้อผิดพลาดในการดำเนินการ');
    } finally {
      setUntrackingId(null);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleShowHiddenItemsChange = (event) => {
    setShowHiddenItems(event.target.checked);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handlers for multiple items (each has url + target price)
  const handleUrlChange = (index, value) => {
    const updated = [...newNegotiation.items];
    updated[index] = { ...updated[index], url: value };
    setNewNegotiation(prev => ({ ...prev, items: updated }));
  };

  const handlePriceChange = (index, value) => {
    const updated = [...newNegotiation.items];
    updated[index] = { ...updated[index], price: value };
    setNewNegotiation(prev => ({ ...prev, items: updated }));
  };

  const handleAddItem = () => {
    setNewNegotiation(prev => ({ ...prev, items: [...prev.items, { url: '', price: '' }] }));
  };

  const handleRemoveItem = (index) => {
    if (newNegotiation.items.length === 1) return;
    const updated = newNegotiation.items.filter((_, i) => i !== index);
    setNewNegotiation(prev => ({ ...prev, items: updated }));
  };

  const handleSocialIdClick = (customerId, existingSocialId) => {
    setCurrentCustomerId(customerId);
    setCurrentSocialId(existingSocialId || '');
    setSocialIdDialogOpen(true);
  };

  const handleSocialIdDialogClose = () => {
    setSocialIdDialogOpen(false);
    setCurrentSocialId('');
    setCurrentCustomerId(null);
  };

  const handleSocialIdSave = async () => {
    if (!currentCustomerId) return;
    const result = await updateSocialId({
      customerId: currentCustomerId,
      socialId: currentSocialId.trim()
    });
    if (result) {
      toast.success('อัพเดท Social ID สำเร็จ');
      handleSocialIdDialogClose();
      await getNegotiationLists(currentPage, showHiddenItems, showHiddenItems);
    } else {
      toast.error('อัพเดท Social ID ไม่สำเร็จ');
    }
  };

  // Add handler for customer contact change
  const handleCustomerContactChange = (smileName) => {
    setNewNegotiation(prev => ({
      ...prev,
      smileName: smileName
    }));
  };

  // Change-customer dialog state
  const [changeCustomerDialogOpen, setChangeCustomerDialogOpen] = useState(false);
  const [changeCustomerFavoriteId, setChangeCustomerFavoriteId] = useState(null);
  const [changeCustomerId, setChangeCustomerId] = useState(null);
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);

  const handleOpenChangeCustomerDialog = (favoriteId) => {
    setChangeCustomerFavoriteId(favoriteId);
    setChangeCustomerId(null);
    setChangeCustomerDialogOpen(true);
  };

  const handleCloseChangeCustomerDialog = () => {
    setChangeCustomerDialogOpen(false);
    setChangeCustomerFavoriteId(null);
    setChangeCustomerId(null);
  };

  const handleSaveChangeCustomer = async () => {
    if (!changeCustomerFavoriteId) return;
    setIsSavingCustomer(true);
    try {
      const success = await updateNegotiationCustomer(changeCustomerFavoriteId, changeCustomerId);
      if (success) {
        toast.success('อัพเดทลูกค้าสำเร็จ');
        handleCloseChangeCustomerDialog();
        await getNegotiationLists(currentPage, showHiddenItems, showHiddenItems);
      } else {
        toast.error('อัพเดทลูกค้าไม่สำเร็จ');
      }
    } catch {
      toast.error('เกิดข้อผิดพลาด');
    } finally {
      setIsSavingCustomer(false);
    }
  };

  // Render functions for each tab
  const renderPendingChangesTab = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Item URL</TableCell>
            <TableCell>Customer Contact</TableCell>
            <TableCell>Mercari ID</TableCell>
            <TableCell>Note</TableCell>
            <TableCell>Changes</TableCell>
            <TableCell>Admin</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pendingChanges.map((change) => (
            <TableRow key={change.id}>
              <TableCell>{change.id}</TableCell>
              <TableCell>
                {!change.handler ? (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleUrlClick(change.id, change.mercariItem?.itemUrl)}
                    disabled={takingCaseId === change.id}
                    sx={{ textDecoration: 'none' }}
                  >
                    {takingCaseId === change.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      change.mercariItem?.itemUrl
                    )}
                  </Link>
                ) : (
                  <a href={change.mercariItem?.itemUrl} target="_blank" rel="noopener noreferrer">
                    {change.mercariItem?.itemUrl}
                  </a>
                )}
              </TableCell>
              <TableCell>
                {change.customerContact ? (
                  change.customerContact
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>
                {change.orderByLabel ? (
                  <Chip label={change.orderByLabel} size="small" color="primary" variant="outlined" />
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>
                {change.note ? (
                  <Chip
                    label={change.note}
                    color="info"
                    size="medium"
                    sx={{
                      fontSize: '1rem',
                      height: 'auto',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        padding: '8px 12px'
                      }
                    }}
                  />
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>
                {renderChangeDetails(change)}
              </TableCell>
              <TableCell>
                {change.handler && (
                  <Chip
                    label={change.handler.nickname}
                    color={change.handler.nickname === currentUsername ? "primary" : "secondary"}
                    variant={change.handler.nickname === currentUsername ? "filled" : "outlined"}
                    size="small"
                  />
                )}
              </TableCell>
              <TableCell>
                {change.createdAt && format(new Date(change.createdAt), 'MMM d, yyyy HH:mm')}
              </TableCell>
              <TableCell>
                {change.handler?.nickname === currentUsername && (
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => handleCompleteCase(change.id)}
                    disabled={completingCaseId === change.id}
                  >
                    {completingCaseId === change.id ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'Done'
                    )}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderPriceNegotiationTab = () => (
    <Grid container spacing={2}>
      {/* Table Section (2/3 width) - Now on the left */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Price Negotiations
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showHiddenItems}
                  onChange={handleShowHiddenItemsChange}
                  size="small"
                />
              }
              label="แสดงสินค้าหมด/ถูกลบและเลิกติดตาม"
            />
          </Box>

          {/* Who asked for a discount on this link? */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="วางลิงก์ Mercari เพื่อดูว่าใครขอต่อราคา"
              value={lookupUrl}
              onChange={(e) => setLookupUrl(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleLookupByUrl();
                }
              }}
              InputProps={{
                endAdornment: lookupUrl && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => { setLookupUrl(''); setLookupResult(null); }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleLookupByUrl}
              disabled={!lookupUrl.trim() || isLookingUp}
              sx={{ minWidth: '100px' }}
            >
              {isLookingUp ? <CircularProgress size={20} /> : 'ใครขอ?'}
            </Button>
          </Box>
          {lookupResult && (
            <Box sx={{ mb: 2 }}>
              {!lookupResult.item ? (
                <Alert severity="warning">ไม่พบสินค้านี้ในระบบ ({lookupResult.itemId})</Alert>
              ) : lookupResult.requests.length === 0 ? (
                <Alert severity="info">ไม่มีคำขอต่อราคาสำหรับ {lookupResult.item.title}</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ลูกค้า</TableCell>
                        <TableCell>ช่องทาง</TableCell>
                        <TableCell>LINE</TableCell>
                        <TableCell>ราคา</TableCell>
                        <TableCell>สถานะ</TableCell>
                        <TableCell>ผู้รับเคส</TableCell>
                        <TableCell>เวลาขอ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lookupResult.requests.map((r) => {
                        const lineId = r.customer?.lineUserId || r.customer?.socialId;
                        return (
                          <TableRow key={`${r.source}-${r.negotiationId || r.favoriteId}`}>
                            <TableCell>
                              {r.customer?.contact ? (
                                r.customer.smileName ? (
                                  <Link
                                    href={`/customer-profile?smileName=${encodeURIComponent(r.customer.smileName)}${r.customer.shop ? `&shop=${encodeURIComponent(r.customer.shop)}` : ''}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {r.customer.contact}
                                  </Link>
                                ) : r.customer.contact
                              ) : (r.customer?.nickname || '-')}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={r.source === 'platform' ? 'แพลตฟอร์ม' : (r.customer?.channel || 'แชท')} color={r.source === 'platform' ? 'primary' : 'default'} />
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {lineId ? (
                                <>
                                  {lineId}
                                  <IconButton size="small" title="คัดลอก" onClick={() => copyToClipboard(lineId)}>📋</IconButton>
                                </>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {r.originalPrice != null ? `¥${r.originalPrice.toLocaleString()} → ¥${(r.proposedPrice ?? '-').toLocaleString()}` : '-'}
                            </TableCell>
                            <TableCell>{r.status || (r.isUntracked ? 'เลิกติดตาม' : 'ติดตามอยู่')}</TableCell>
                            <TableCell>{r.handledBy || '-'}</TableCell>
                            <TableCell>{r.requestedAt ? format(new Date(r.requestedAt), 'dd/MM/yyyy HH:mm') : '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Untrack Input */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="ใส่รายการที่ต้องการยกเลิกติดตาม"
              value={untrackSearchUrl}
              onChange={(e) => setUntrackSearchUrl(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleUntrackByUrl();
                }
              }}
              InputProps={{
                endAdornment: untrackSearchUrl && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setUntrackSearchUrl('')}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleUntrackByUrl}
              disabled={!untrackSearchUrl.trim() || untrackingId !== null}
              sx={{ minWidth: '100px' }}
            >
              {untrackingId ? <CircularProgress size={20} /> : 'เลิกติดตาม'}
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item URL</TableCell>
                  <TableCell>Customer Contact</TableCell>
                  <TableCell>Mercari ID</TableCell>
                  <TableCell>Social ID (LINE)</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>ซื้อเลย</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Request Time</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoadingNegotiations ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : negotiationError ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Alert severity="error">{negotiationError}</Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  negotiationLists && negotiationLists.length > 0 && [...negotiationLists].sort((a, b) => new Date(b.date) - new Date(a.date)).map((item, index) => (
                    <TableRow key={item.id || index} sx={{ opacity: item.isUntracked ? 0.6 : 1 }}>
                      <TableCell>
                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                          {item.url}
                        </a>
                        {item.isUntracked && (
                          <Chip label="เลิกติดตาม" size="small" color="warning" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{item.customerContact || '-'}</span>
                          <IconButton
                            size="small"
                            title="เปลี่ยนลูกค้า"
                            onClick={() => handleOpenChangeCustomerDialog(item.id)}
                            sx={{ color: '#1976d2', '&:hover': { backgroundColor: '#e3f2fd' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {item.orderByLabel ? (
                          <Chip label={item.orderByLabel} size="small" color="primary" variant="outlined" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {item.customerId && (item.channel || '').toString().toLowerCase() === 'line' ? (
                          <IconButton
                            size="small"
                            onClick={() => handleSocialIdClick(item.customerId, item.socialId || '')}
                            title={item.socialId ? 'LINE Social ID มีอยู่ - คลิกเพื่อแก้ไข' : 'ไม่มี LINE Social ID - คลิกเพื่อเพิ่ม (สำหรับส่งแจ้งเตือนผ่าน Chatcone)'}
                          >
                            {item.socialId ? (
                              <TaskAltIcon style={{ color: '#4caf50', fontSize: '1.25rem' }} />
                            ) : (
                              <RadioButtonUncheckedIcon style={{ color: '#9e9e9e', fontSize: '1.25rem' }} />
                            )}
                          </IconButton>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {item.price != null ? `¥${item.price}` : '-'}
                      </TableCell>
                      <TableCell>
                        {item.urgentPurchase ? (
                          <Chip label="ซื้อเลย" size="small" color="error" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.status} 
                          color={item.status === 'sold_out' ? 'error' : 'default'}
                          size="small"
                          sx={{
                            backgroundColor: item.status === 'sold_out' ? 'error.main' : 'grey.300',
                            color: item.status === 'sold_out' ? 'white' : 'grey.700',
                            '&:hover': {
                              backgroundColor: item.status === 'sold_out' ? 'error.dark' : 'grey.400',
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {format(new Date(item.date), 'MMM d, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color={item.isUntracked ? "warning" : "error"}
                          onClick={() => handleUntrackById(item.id, item.isUntracked)}
                          disabled={untrackingId === item.id}
                          title={item.isUntracked ? "เริ่มติดตามอีกครั้ง" : "เลิกติดตาม - ไม่สนใจรายการนี้แล้ว"}
                        >
                          {untrackingId === item.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CloseIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {negotiationPagination && negotiationPagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={negotiationPagination.totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {negotiationPagination && (
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
              หน้า {negotiationPagination.currentPage} จาก {negotiationPagination.totalPages} 
              {' '}({negotiationPagination.totalItems} รายการ)
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Form Section (1/3 width) - Now on the right */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Add New Negotiation
          </Typography>
          <form onSubmit={handleNegotiationSubmit}>
            {/* Profile selector at top */}
            <Box sx={{ mb: 2 }}>
              <ProfileCombo
                key={resetKey}
                profiles={profiles}
                onChange={handleCustomerContactChange}
              />
            </Box>

            {/* Mercari Account selector */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Mercari Account (ID ที่ต่อราคา)</InputLabel>
                <Select
                  value={newNegotiation.orderByOptionId}
                  label="Mercari Account (ID ที่ต่อราคา)"
                  onChange={(e) => setNewNegotiation(prev => ({ ...prev, orderByOptionId: e.target.value }))}
                  disabled={isLoadingNegotiations || isSubmitting}
                >
                  <MenuItem value=""><em>ไม่ระบุ</em></MenuItem>
                  {orderByOptions.map(acc => (
                    <MenuItem key={acc.id} value={acc.id}>{acc.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Urgent purchase: once per form */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newNegotiation.urgentPurchase}
                    onChange={(e) => setNewNegotiation(prev => ({ ...prev, urgentPurchase: e.target.checked }))}
                    disabled={isLoadingNegotiations || isSubmitting}
                  />
                }
                label="ลดแล้ว ซื้อได้เลย"
              />
            </Box>

            {/* Per-link: URL + target price (¥) */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Mercari items ({newNegotiation.items.filter(i => i.url.trim()).length} / {newNegotiation.items.length})
            </Typography>
            {newNegotiation.items.map((item, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 24 }}>
                    {index + 1}.
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={item.url}
                    onChange={(e) => handleUrlChange(index, e.target.value)}
                    placeholder="https://jp.mercari.com/item/..."
                    disabled={isLoadingNegotiations || isSubmitting}
                  />
                  {newNegotiation.items.length > 1 && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveItem(index)}
                      disabled={isLoadingNegotiations || isSubmitting}
                      sx={{ minWidth: 'auto' }}
                    >
                      ✕
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 3 }}>
                  <TextField
                    size="small"
                    label="Target price (¥)"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    value={item.price}
                    onChange={(e) => handlePriceChange(index, e.target.value)}
                    placeholder="e.g. 1000"
                    disabled={isLoadingNegotiations || isSubmitting}
                    sx={{ width: 140 }}
                  />
                </Box>
              </Box>
            ))}
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddItem}
              disabled={isLoadingNegotiations || isSubmitting}
              sx={{ mb: 2 }}
            >
              + Add item
            </Button>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 1 }}
              disabled={isLoadingNegotiations || isSubmitting || newNegotiation.items.every(i => i.url.trim() === '')}
            >
              {isLoadingNegotiations || isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                `Add Negotiation${newNegotiation.items.filter(i => i.url.trim()).length > 1 ? 's' : ''}`
              )}
            </Button>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );

  // First load only — see initialLoading above.
  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const visibleError = error && error !== dismissedError ? error : null;

  // Main render
  return (
    <Box p={3}>
      {visibleError && (
        <Alert severity="error" onClose={() => setDismissedError(error)} sx={{ mb: 2 }}>
          {visibleError}
        </Alert>
      )}
      <Typography variant="h5" gutterBottom>
        Mercari Management
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="contained"
          onClick={() => setTranslateIsOpen(true)}
          sx={{ backgroundColor: '#4f46e5', '&:hover': { backgroundColor: '#4338ca' } }}
        >
          แปลภาษา
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="รายการ ราคาเปลี่ยนแปลง" />
          <Tab label="รายการต่อราคา" />
        </Tabs>
      </Box>

      {tabValue === 0 && renderPendingChangesTab()}
      {tabValue === 1 && renderPriceNegotiationTab()}

      <Dialog open={translateIsOpen} onClose={() => setTranslateIsOpen(false)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <Translate />
        </DialogContent>
      </Dialog>

      {/* Change Customer Dialog */}
      <Dialog open={changeCustomerDialogOpen} onClose={handleCloseChangeCustomerDialog} maxWidth="sm" fullWidth>
        <DialogTitle>เปลี่ยนลูกค้า</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <ProfileCombo
              profiles={profiles}
              onChange={(smileName) => {
                const found = profiles.find(p => p.smileName === smileName);
                setChangeCustomerId(found ? found.id : null);
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangeCustomerDialog}>ยกเลิก</Button>
          <Button
            onClick={handleSaveChangeCustomer}
            variant="contained"
            color="primary"
            disabled={isSavingCustomer || changeCustomerId == null}
          >
            {isSavingCustomer ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={socialIdDialogOpen} onClose={handleSocialIdDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>แก้ไข Social ID</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Social ID"
            fullWidth
            variant="outlined"
            value={currentSocialId}
            onChange={(e) => setCurrentSocialId(e.target.value)}
            helperText="กรอก Social ID (เช่น LINE User ID) หรือเว้นว่างเพื่อลบ"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSocialIdDialogClose}>ยกเลิก</Button>
          <Button onClick={handleSocialIdSave} variant="contained" color="primary">
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MercariChange;